// AppContent.js
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { setBotWaitLoader } from './state/actions/loader';
import { SocketProvider } from '../src/services/socket/socketContext';
import { getAuthData, getSessinIdFromLS, updateDataInStorage } from './services/storage/LocalStorage';
import { getPageName, setPageTitle, updateAppHead } from './utils/general';
import { useNavigate } from 'react-router-dom';
import { BANNER_TYPE_INFO } from './utils/constants';
import { UserServiceClientApi } from './services/api/UserApiServices';
import { getStripeAmount } from './utils/Helpers';
import { showToast } from './components/ui/toaster';
import { RechargeContext } from './RechargeContext';
import { useOnlineStatus } from './services/customHooks/useOnlineStatus';
import { getUserDomainList, setDomainState, setSelectedDomain } from './state/actions/domain';
import { usePreloadCachedImages } from './components/common/CacheAssets/usePreloadCachedImages';
import { fetchWalletBalanceService, sendMsgForBanner, setUpdateLoggedUserDetails, setLoggedUserDetails } from './state/actions/user';

import UpdateModal from './components/ui/Modals/UpdateModal';
import AppRoutes from './routing/AppRoutes';
import ModalPopup from './components/ui/Modals/ModalMessages/ModalPopup';
import GetCreditModal from './components/ui/Modals/Payments/GetCreditModal';
import ConfrimCreditModal from './components/ui/Modals/Payments/ConfrimCreditModal';
import StripePaymentsNew from './components/ui/Modals/Payments/StripePayments_New';
import { getDomainConfig } from './utils/DomainsHelper';


const AppContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authData = getAuthData();
  const pathname = getPageName();
  const sessionId = getSessinIdFromLS();

  const domainState = useSelector((state) => state.domain.domainState);
  const [lastLoggedInDomain, setLastLoggedInDomain] = useState(null);
  const [showNoBalance, setShowNoBalance] = useState(false);
  const [getCreditModal, setGetCreditModal] = useState(false);
  const [getConfirmCreditModal, setGetConfirmCreditModal] = useState(false);
  const [showStripe, setShowStripe] = useState(false);
  const [codeAlreadyApplied, setCodeAlreadyApplied] = useState(false);

  const [amountToCredit, setAmountToCredit] = useState(0);
  const [topUpAmount, setTopUpAmount] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [userVoipStatus, setUserVoipStatus] = useState({});

  usePreloadCachedImages(); //cache assets as key value
  useOnlineStatus();        //To get user network status


  const getDomain = async () => {
    try {
      dispatch(setBotWaitLoader(true));
      const domainList = await getUserDomainList();

      if (!Array.isArray(domainList) || domainList.length === 0) {
        console.warn("No domains found");
        return;
      }

      console.log("%c Domains list ", "color: green;", domainList);

      const lastLoggedDomain = Object.assign({}, ...domainList.filter((d) => d.lastLoggedIn));
      console.log("%c Last Login Domain : ", "color: green;", lastLoggedDomain);

      if (lastLoggedDomain) {
        setLastLoggedInDomain(lastLoggedDomain);
        dispatch(setSelectedDomain(lastLoggedDomain));
        if (pathname !== "Home") {
          dispatch(setBotWaitLoader(false));
        }
      }
      else {
        console.warn("No last logged-in domain found");
        dispatch(setBotWaitLoader(false));
      }
    } catch (err) {
      console.error("Error fetching domain list", err);
    }
  };


  useEffect(() => {
    setTimeout(() => {
      if ((authData && Object.keys(authData).length > 0 && sessionId) && domainState) {
        getDomain();
        dispatch(setUpdateLoggedUserDetails(authData)); //set Logged user Data in redux
        dispatch(setDomainState(false))
      }
    }, 2000)
  }, [domainState]);

  useEffect(() => {
    const onLoad = async () => {
      if (authData && Object.keys(authData).length > 0 && sessionId) {
        // If LoggedState exists but sessionId is missing inside it → update it
        if (!authData.auth?.sessionId) {
          const updateUserData = {
            ...authData,
            auth: {
              ...authData.auth,
              sessionId,
            },
          };

          await updateDataInStorage("LoggedState", updateUserData);
        }

        dispatch(setLoggedUserDetails(authData));
        getDomain();
        dispatch(fetchWalletBalanceService())
      }
    };

    window.addEventListener("load", onLoad);
    return () => window.removeEventListener("load", onLoad);
  }, []);


  useEffect(() => {
    setPageTitle()
  }, [navigate]);


  useEffect(() => {
    if (window.location.pathname.includes('contacts')) {
      window.document.title = "Contacts";
    } else if (window.location.pathname.includes('profile')) {
      window.document.title = "My Profile";
    } else {
      const config = getDomainConfig();
      updateAppHead({
        title: config.title,
        favicon: config.favicon,
      });
    }
  }, [window.location.pathname])

  const openRecharge = () => {
    dispatch(sendMsgForBanner(BANNER_TYPE_INFO, 'Your account has no balance. Please top up your account.'));
    setTimeout(() => {
      setGetCreditModal(true);
    }, 100);
  };

  const addCredit = (credit, alreadyApplied) => {
    setAmountToCredit(credit);
    setCodeAlreadyApplied(alreadyApplied);
    setGetConfirmCreditModal(true);
  };

  const handleStripePayment = (amount, topUp, tax) => {
    setGetCreditModal(false);
    setGetConfirmCreditModal(false);
    if (amount <= 0) return;
    setTopUpAmount(topUp);
    setTaxAmount(tax);
    setShowStripe(true);
  };

  const initiatePayment = (paymentMethodId) => {
    const currency = 'USD';
    return UserServiceClientApi.initiateStripePayment({
      paymentMethodId,
      topupAmount: getStripeAmount(topUpAmount),
      taxAmount: getStripeAmount(taxAmount),
      currency,
      paymentCode: '100',
    });
  };

  const confirmPayment = (paymentIntentId) => {
    const currency = 'USD';
    return UserServiceClientApi.confirmStripePayment({
      paymentIntentId,
      topupAmount: getStripeAmount(topUpAmount),
      taxAmount: getStripeAmount(taxAmount),
      currency,
      paymentCode: '100',
    });
  };

  const sendPaymentResponseMessage = (response) => {
    if (response === true) {
      dispatch(fetchWalletBalanceService())
      setShowStripe(false);
      showToast('success', 'Account topped up successfully.');
    }
  };

  return (
    <RechargeContext.Provider value={{ openRecharge }}>
      <SocketProvider lastLoggedInDomain={lastLoggedInDomain}>
        <UpdateModal />

        {/* Modals for recharge */}
        {showNoBalance && !userVoipStatus?.isPostpaidUser && (
          <ModalPopup onClose={() => setShowNoBalance(false)} size="sm">
            <div className="d-flex justify-content-center align-items-center flex-column" style={{ textAlign: 'center' }}>
              <h5>Your account has no balance.<br />Please top up your account.</h5>
              <a
                className="btn-open w-25"
                onClick={() => {
                  setShowNoBalance(false);
                  setGetCreditModal(true);
                }}
              >
                OK
              </a>
            </div>
          </ModalPopup>
        )}

        {getCreditModal && !userVoipStatus?.isPostpaidUser && (
          <ModalPopup onClose={() => setGetCreditModal(false)} size="md" noHeader="true" title="Top-up">
            <GetCreditModal
              user={authData}
              addCredit={addCredit}
              cancel={() => setGetCreditModal(false)}
              codeAlreadyApplied={codeAlreadyApplied}
            />
          </ModalPopup>
        )}

        {getConfirmCreditModal && (
          <ModalPopup noHeader="true" size="2xl">
            <ConfrimCreditModal
              amountToCredit={amountToCredit}
              onClose={() => setGetConfirmCreditModal(false)}
              handleStripe={handleStripePayment}
              codeAlreadyApplied={codeAlreadyApplied}
            />
          </ModalPopup>
        )}

        {showStripe && (
          <ModalPopup onClose={() => setShowStripe(false)} size="sm" noHeader="true">
            <StripePaymentsNew
              user={authData}
              startPayment={initiatePayment}
              confirmPayment={confirmPayment}
              sendPaymentResponseMessage={sendPaymentResponseMessage}
            />
          </ModalPopup>
        )}
        <AppRoutes />
      </SocketProvider>
    </RechargeContext.Provider>
  );
};

export default AppContent;
